import { createSelector } from 'reselect';
import { IRegulatoryResource } from 'state/regulatoryDocuments/regulatoryDocuments.model';
import { getQuoteRegulationInfos } from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { WorkspaceType } from 'state/workspaces/model';
import { isQuoteWorkspaceType } from 'state/workspaces/model';

import type { RegulationDocument } from './model';

export const getQuoteRegulationInfosSelector = (state: AppState, workspaceType: WorkspaceType, tabId: string) =>
  isQuoteWorkspaceType(workspaceType) ? getQuoteRegulationInfos(state, workspaceType, tabId) : undefined;

export function asDocuments(documents: IRegulatoryResource[]): RegulationDocument[] {
  const privateDocuments = documents.filter(
    d => d.isPublic === false && d.documentToken !== undefined && d.uri === undefined,
  );
  const publicDocuments = documents.filter(
    d => d.isPublic !== false && d.uri !== undefined && d.language !== undefined && d.documentToken === undefined,
  );
  return privateDocuments.length > 0 ? privateDocuments : publicDocuments;
}

function toEitherPrivateOrPublicDocuments(documents: IRegulatoryResource[]): RegulationDocument[] {
  const privateDocuments = documents.filter(d => d.isPublic === false && d.documentToken !== undefined);
  const publicDocuments = documents.filter(d => d.isPublic !== false && d.uri !== undefined);

  return privateDocuments.length > 0 ? privateDocuments : publicDocuments;
}

// --- Termsheet ---

export const getTermsheetDocuments = createSelector([getQuoteRegulationInfosSelector], infos =>
  toEitherPrivateOrPublicDocuments(infos?.termsheetDocument ?? []),
);

export const getTermsheetDocumentError = createSelector(
  [getQuoteRegulationInfosSelector],
  infos => infos?.termsheetDocumentError,
);

export const getTermsheetDocumentTimeout = createSelector(
  [getQuoteRegulationInfosSelector],
  infos => infos?.termsheetDocumentTimeout ?? false,
);

const hasTermsheetDocuments = createSelector([getTermsheetDocuments], documents => (documents?.length ?? 0) > 0);

export const hasTermsheetError = createSelector([getTermsheetDocumentError], errors => (errors?.length ?? 0) > 0);

const getHasTermsheet = createSelector(
  [hasTermsheetDocuments, hasTermsheetError, getTermsheetDocumentTimeout],
  (hasDocs, hasError, hasTimeout) => hasDocs || hasTimeout || hasError,
);

// --- KID ---

export const getKidDocuments = createSelector([getQuoteRegulationInfosSelector], infos =>
  toEitherPrivateOrPublicDocuments(infos?.kidDocument ?? []),
);

export const getKidDocumentError = createSelector([getQuoteRegulationInfosSelector], infos => infos?.kidDocumentError);

export const getKidDocumentTimeout = createSelector(
  [getQuoteRegulationInfosSelector],
  infos => infos?.kidDocumentTimeout ?? false,
);

const hasKidDocuments = createSelector([getKidDocuments], documents => (documents?.length ?? 0) > 0);

export const hasKidError = createSelector([getKidDocumentError], errors => (errors?.length ?? 0) > 0);

const getHasKid = createSelector(
  [hasKidDocuments, hasKidError, getKidDocumentTimeout],
  (hasDocs, hasError, hasTimeout) => hasDocs || hasTimeout || hasError,
);

// --- Product Governance ---

export const getProductGovernanceDocuments = createSelector([getQuoteRegulationInfosSelector], infos =>
  toEitherPrivateOrPublicDocuments(infos?.productGovernanceDocumentation ?? []),
);

export const getProductGovernanceDocumentError = createSelector(
  [getQuoteRegulationInfosSelector],
  infos => infos?.productGovernanceDocumentationError,
);

export const getProductGovernanceDocumentTimeout = createSelector(
  [getQuoteRegulationInfosSelector],
  infos => infos?.productGovernanceDocumentationTimeout ?? false,
);

const hasProductGovernanceDocuments = createSelector(
  [getProductGovernanceDocuments],
  documents => (documents?.length ?? 0) > 0,
);

export const hasProductGovernanceError = createSelector(
  [getProductGovernanceDocumentError],
  errors => (errors?.length ?? 0) > 0,
);

const getHasProductGovernance = createSelector(
  [hasProductGovernanceDocuments, hasProductGovernanceError, getProductGovernanceDocumentTimeout],
  (hasDocs, hasError, hasTimeout) => hasDocs || hasTimeout || hasError,
);

// --- Visible count ---

export const getVisibleDocumentCount = createSelector(
  [getHasTermsheet, getHasKid, getHasProductGovernance],
  (hasTermsheet, hasKid, hasProductGovernance) => {
    return [hasTermsheet, hasKid, hasProductGovernance].filter(Boolean).length;
  },
);
